import { type MetricFailureReason, type MetricOutcome } from '@/models/metrics.ts';

/**
 * How far an arrive/check-in flow should go for the requesting caller.
 * ARRIVE = arrive only (the service still auto-checks-in when the dispatch config requires it).
 * CHECK_IN = complete the check-in; the caller has committed to it, so incomplete forms do not block
 * the write (a forms code is still generated). Only reachable with incomplete forms via a drag into a
 * treatment room; the check-in button routes patients with forms to ARRIVE.
 */
export enum CheckinIntent {
    ARRIVE = 'arrive',
    CHECK_IN = 'check_in',
}

/** Per-appointment check-in progress held in the client store. */
export type AppointmentCheckinState = {
    is_checking_in: boolean;
};

/** The terminal state an arrive/check-in flow settled into. */
export enum CheckinOutcomeKind {
    CHECKED_IN = 'checked_in',
    ARRIVED = 'arrived',
    AWAITING_FORMS = 'awaiting_forms',
    CANCELLED = 'cancelled',
    FAILED = 'failed',
}

/**
 * Result of an arrive/check-in flow. The service owns the terminal CHECKED_IN write and reports
 * back what it did, so callers react to the outcome instead of performing the write themselves.
 */
export type CheckinOutcome =
    | { kind: CheckinOutcomeKind.CHECKED_IN; ticket: string }
    | { kind: CheckinOutcomeKind.ARRIVED; ticket: string }
    | { kind: CheckinOutcomeKind.AWAITING_FORMS; ticket: string }
    | { kind: CheckinOutcomeKind.CANCELLED }
    | { kind: CheckinOutcomeKind.FAILED; reason?: MetricFailureReason };

/**
 * Which outcomes are terminal and how each maps onto the metric vocabulary. `undefined` marks an
 * intermediate outcome, reported later by the check-in that completes it. The full `Record` forces every
 * new outcome kind to be classified here.
 */
export const OUTCOME_METRIC = {
    [CheckinOutcomeKind.CHECKED_IN]: 'success',
    [CheckinOutcomeKind.FAILED]: 'failure',
    [CheckinOutcomeKind.CANCELLED]: 'abort',
    [CheckinOutcomeKind.ARRIVED]: undefined,
    [CheckinOutcomeKind.AWAITING_FORMS]: undefined,
} as const satisfies Record<CheckinOutcomeKind, MetricOutcome | undefined>;

type TerminalOutcomeKind = {
    [K in CheckinOutcomeKind]: (typeof OUTCOME_METRIC)[K] extends MetricOutcome ? K : never;
}[CheckinOutcomeKind];

/** A `CheckinOutcome` that ends the flow, so it is the one reported to `careflow.checkin.outcome`. */
export type TerminalCheckinOutcome = Extract<CheckinOutcome, { kind: TerminalOutcomeKind }>;
